import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api-client';
import { useWebSocket } from './WebSocketContext';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

/**
 * NotificationContext - Centralized notification management
 *
 * Handles:
 * - Fetching and polling notifications from the API
 * - Managing notification state (unread count, active notifications)
 * - Triggering modals based on notification types
 * - Marking notifications as read/dismissed/actioned
 */
export function NotificationProvider({ children }) {
  const ws = useWebSocket();
  const { token, user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeModal, setActiveModal] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (options = {}) => {
    try {
      const params = new URLSearchParams();
      if (options.status) params.append('status', options.status);
      if (options.type) params.append('type', options.type);
      if (options.limit) params.append('limit', options.limit);

      console.log('[NotificationContext] Fetching notifications with params:', params.toString());
      const response = await api.get(`/api/v1/notifications?${params.toString()}`);
      console.log('[NotificationContext] Received notifications:', response);

      // Set notifications from backend
      setNotifications(response);

      // Calculate unread count
      const unreadCount = response.filter(n => n.status === 'unread').length;
      setUnreadCount(unreadCount);

      console.log('[NotificationContext] Updated notifications:', {
        total: response.length,
        unread: unreadCount
      });

      return response;
    } catch (error) {
      console.error('[NotificationContext] Failed to fetch notifications:', error);
      console.error('[NotificationContext] Error details:', error.response || error.message);
      return [];
    }
  }, []);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/notifications/unread-count');
      setUnreadCount(response.count);
      return response.count;
    } catch (error) {
      console.error('[NotificationContext] Failed to fetch unread count:', error);
      return 0;
    }
  }, []);

  // Show modal for notification
  const showModal = useCallback((notification) => {
    console.log('[NotificationContext] Showing modal for notification:', notification);
    setActiveModal(notification);
  }, []);

  // Close active modal
  const closeModal = useCallback(() => {
    console.log('[NotificationContext] Closing modal');
    setActiveModal(null);
  }, []);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const notification = notifications.find(n => n.id === notificationId);

      if (!notification) {
        console.warn('[NotificationContext] Notification not found:', notificationId);
        return;
      }

      // Update on backend
      const response = await api.put(`/api/v1/notifications/${notificationId}/status`, {
        status: 'read'
      });

      // Update local state
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? response : n)
      );

      // Decrement unread count
      if (notification.status === 'unread') {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      return response;
    } catch (error) {
      console.error('[NotificationContext] Failed to mark as read:', error);
      throw error;
    }
  }, [notifications]);

  // Mark notification as dismissed
  const markAsDismissed = useCallback(async (notificationId) => {
    try {
      const notification = notifications.find(n => n.id === notificationId);

      if (!notification) {
        console.warn('[NotificationContext] Notification not found:', notificationId);
        return;
      }

      // Update on backend
      const response = await api.put(`/api/v1/notifications/${notificationId}/status`, {
        status: 'dismissed'
      });

      // Update local state
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? response : n)
      );

      // Decrement unread count if it was unread
      if (notification.status === 'unread') {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      // Close modal if this notification is active
      if (activeModal?.id === notificationId) {
        closeModal();
      }

      return response;
    } catch (error) {
      console.error('[NotificationContext] Failed to mark as dismissed:', error);
      throw error;
    }
  }, [notifications, activeModal, closeModal]);

  // Mark notification as actioned
  const markAsActioned = useCallback(async (notificationId) => {
    try {
      const notification = notifications.find(n => n.id === notificationId);

      if (!notification) {
        console.warn('[NotificationContext] Notification not found:', notificationId);
        return;
      }

      // Update on backend
      const response = await api.put(`/api/v1/notifications/${notificationId}/status`, {
        status: 'actioned'
      });

      // Update local state
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? response : n)
      );

      // Decrement unread count if it was unread
      if (notification.status === 'unread') {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      // Close modal if this notification is active
      if (activeModal?.id === notificationId) {
        closeModal();
      }

      return response;
    } catch (error) {
      console.error('[NotificationContext] Failed to mark as actioned:', error);
      throw error;
    }
  }, [notifications, activeModal, closeModal]);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      const notification = notifications.find(n => n.id === notificationId);

      if (!notification) {
        console.warn('[NotificationContext] Notification not found:', notificationId);
        return;
      }

      // Delete on backend
      await api.delete(`/api/v1/notifications/${notificationId}`);

      // Update local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));

      // Decrement unread count if it was unread
      if (notification.status === 'unread') {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      // Close modal if this notification is active
      if (activeModal?.id === notificationId) {
        closeModal();
      }
    } catch (error) {
      console.error('[NotificationContext] Failed to delete notification:', error);
      throw error;
    }
  }, [notifications, activeModal, closeModal]);

  // Clear all notifications
  const clearAll = useCallback(async () => {
    try {
      // Delete all notifications on backend
      const deletePromises = notifications.map(notification =>
        api.delete(`/api/v1/notifications/${notification.id}`)
      );
      await Promise.all(deletePromises);

      // Clear local state
      setNotifications([]);
      setUnreadCount(0);
      closeModal();

      console.log('[NotificationContext] Cleared all notifications');
    } catch (error) {
      console.error('[NotificationContext] Failed to clear all notifications:', error);
      throw error;
    }
  }, [notifications, closeModal]);

  // Handle WebSocket notification
  const handleWebSocketNotification = useCallback((data) => {
    console.log('[NotificationContext] WebSocket notification received:', data);
    // Fetch latest notifications from backend
    fetchNotifications({ limit: 50 });
  }, [fetchNotifications]);

  // Initial fetch on mount - ONLY if authenticated
  useEffect(() => {
    if (!token || !user) {
      console.log('[NotificationContext] Skipping fetch - user not authenticated');
      return;
    }

    console.log('[NotificationContext] User authenticated - fetching notifications');
    setIsLoading(true);
    // Fetch all notifications (we'll filter on the frontend)
    fetchNotifications({ limit: 50 }).finally(() => {
      setIsLoading(false);
    });
  }, [token, user, fetchNotifications]);

  // Poll for new notifications every 30 seconds - ONLY if authenticated
  useEffect(() => {
    if (!token || !user) {
      console.log('[NotificationContext] Skipping polling - user not authenticated');
      return;
    }

    console.log('[NotificationContext] Starting notification polling (30s interval)');
    const interval = setInterval(() => {
      // Fetch all notifications (we'll filter on the frontend)
      fetchNotifications({ limit: 50 });
    }, 30000);

    return () => {
      console.log('[NotificationContext] Stopping notification polling');
      clearInterval(interval);
    };
  }, [token, user, fetchNotifications]);

  // Clear notifications when user logs out
  useEffect(() => {
    if (!token || !user) {
      console.log('[NotificationContext] User logged out - clearing notifications');
      setNotifications([]);
      setUnreadCount(0);
      setActiveModal(null);
    }
  }, [token, user]);

  // Listen for WebSocket notification messages
  useEffect(() => {
    if (!ws || !ws.on) return;

    console.log('[NotificationContext] Registering WebSocket notification handler');

    const cleanup = ws.on('notification', handleWebSocketNotification);

    return cleanup;
  }, [ws, handleWebSocketNotification]);

  const value = {
    notifications,
    unreadCount,
    activeModal,
    isLoading,
    fetchNotifications,
    fetchUnreadCount,
    showModal,
    closeModal,
    markAsRead,
    markAsDismissed,
    markAsActioned,
    deleteNotification,
    clearAll,
    handleWebSocketNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
